/**
 * Notification Service - Frontend service for notification operations
 */

import api from "../../../shared/api.jsx";

/**
 * Get notifications for an admin
 * @param {Object} filters - Filter criteria
 * @param {boolean} [filters.isRead] - Filter by read status
 * @param {string} [filters.type] - Filter by notification type
 * @param {string} [filters.priority] - Filter by priority
 * @param {string} [filters.entityType] - Filter by entity type
 * @param {string} [filters.category] - Inbox category (action_required, leads, …)
 * @param {string} [filters.search] - Search title/message/entityId
 * @param {number} [filters.limit] - Limit results
 * @param {number} [filters.offset] - Offset for pagination
 * @param {string} [filters.adminId] - Admin ID (defaults to "admin")
 * @returns {Promise<Object>} Notifications data
 */
export const getNotifications = async (filters = {}) => {
  try {
    const params = new URLSearchParams();

    if (filters.isRead !== undefined) {
      params.append("isRead", filters.isRead.toString());
    }
    if (filters.type) {
      params.append("type", filters.type);
    }
    if (filters.priority) {
      params.append("priority", filters.priority);
    }
    if (filters.entityType) {
      params.append("entityType", filters.entityType);
    }
    if (filters.category) {
      params.append("category", filters.category);
    }
    if (filters.search) {
      params.append("search", filters.search);
    }
    if (filters.limit) {
      params.append("limit", filters.limit.toString());
    }
    if (filters.offset) {
      params.append("offset", filters.offset.toString());
    }
    if (filters.adminId) {
      params.append("adminId", filters.adminId);
    }

    const response = await api.get(`/notifications?${params.toString()}`);
    return response;
  } catch (error) {
    return {
      success: false,
      error: error.message,
    };
  }
};

/**
 * Get notification by ID
 * @param {string} notificationId - Notification ID
 * @param {string} [adminId] - Admin ID (defaults to "admin")
 * @returns {Promise<Object>} Notification data
 */
export const getNotificationById = async (
  notificationId,
  adminId = "admin"
) => {
  try {
    const response = await api.get(
      `/notifications/${notificationId}?adminId=${adminId}`
    );
    return response;
  } catch (error) {
    return {
      success: false,
      error: error.message,
    };
  }
};

/**
 * Mark notification as read
 * @param {string} notificationId - Notification ID
 * @param {string} [adminId] - Admin ID (defaults to "admin")
 * @returns {Promise<Object>} Update result
 */
export const markNotificationAsRead = async (
  notificationId,
  adminId = "admin"
) => {
  try {
    const response = await api.put(`/notifications/${notificationId}/read`, {
      adminId,
    });
    return response;
  } catch (error) {
    return {
      success: false,
      error: error.message,
    };
  }
};

/**
 * Mark all notifications as read for an admin
 * @param {string} [adminId] - Admin ID (defaults to "admin")
 * @returns {Promise<Object>} Update result
 */
export const markAllNotificationsAsRead = async (adminId = "admin") => {
  try {
    const response = await api.put("/notifications/read-all", {
      adminId,
    });
    return response;
  } catch (error) {
    return {
      success: false,
      error: error.message,
    };
  }
};

/**
 * Delete notification
 * @param {string} notificationId - Notification ID
 * @param {string} [adminId] - Admin ID (defaults to "admin")
 * @returns {Promise<Object>} Deletion result
 */
export const deleteNotification = async (notificationId, adminId = "admin") => {
  try {
    const response = await api.delete(
      `/notifications/${notificationId}?adminId=${adminId}`
    );
    return response;
  } catch (error) {
    return {
      success: false,
      error: error.message,
    };
  }
};

/**
 * Delete all notifications for an admin
 * @param {string} [adminId] - Admin ID (defaults to "admin")
 * @returns {Promise<Object>} Deletion result
 */
export const deleteAllNotifications = async (adminId = "admin") => {
  try {
    const response = await api.delete(`/notifications?adminId=${adminId}`);
    return response;
  } catch (error) {
    return {
      success: false,
      error: error.message,
    };
  }
};

/**
 * Get notification statistics
 * @param {string} [adminId] - Admin ID (defaults to "admin")
 * @returns {Promise<Object>} Notification statistics
 */
export const getNotificationStats = async (adminId = "admin") => {
  try {
    const response = await api.get(`/notifications/stats?adminId=${adminId}`);
    return response;
  } catch (error) {
    return {
      success: false,
      error: error.message,
    };
  }
};
